import React, { useMemo, useState } from "react"
import { toast } from "react-toastify"

import { Button } from "~/ui-rtk/components/ui/controls"
import { ConnectorDto } from "~/ui-rtk/api/types"
import { useCustomConnectorAdditionalConfig } from "~/ui-rtk/hooks/useCustomConnectorAdditionalConfig"
import CustomConfigField from "~/ui-rtk/components/features/connectors/forms/CustomConnectorConfigForm/CustomConfigField"
import { hasErrorMessage } from "~/ui-rtk/utils/http-utils"

interface CustomConfigSectionProps {
  companyConnectorId: string
  connector?: ConnectorDto
  isSuperAdmin: boolean
}

/**
 * Inline section for the connector info modal that lets a user change
 * post-OAuth selections (e.g. which Instagram account this connector targets)
 * after initial setup. Gated to super admins — collapsed by default, the
 * Edit button reveals the field(s) and Save/Cancel controls.
 */
const CustomConfigSection: React.FC<CustomConfigSectionProps> = ({
  companyConnectorId,
  connector,
  isSuperAdmin,
}) => {
  const {
    schema,
    existingMetadata,
    isLoadingProperties,
    completeSetup,
    isCompletingSetup,
  } = useCustomConnectorAdditionalConfig(companyConnectorId, connector)

  // Seed from saved metadata. We don't merge user edits into existingMetadata;
  // formData is the source of truth once the user touches a field.
  const initialFormData = useMemo<Record<string, string>>(() => {
    const seed: Record<string, string> = {}
    if (!existingMetadata) return seed
    for (const field of schema) {
      const key = field.configuration_path.split(".").pop()
      if (!key) continue
      const saved = (existingMetadata as Record<string, unknown>)[key]
      if (typeof saved === "string") {
        seed[key] = saved
      }
    }
    return seed
  }, [schema, existingMetadata])

  const [formData, setFormData] = useState<Record<string, string>>({})
  const [isEditing, setIsEditing] = useState(false)

  // Initialize formData once the seed is ready and stable.
  // Using a ref via useState lazy init would skip schema-loading; this effect
  // pattern keeps it simple and only fires when seed contents change.
  React.useEffect(() => {
    setFormData(initialFormData)
  }, [initialFormData])

  const isDirty = useMemo(
    () =>
      schema.some(field => {
        const key = field.configuration_path.split(".").pop()
        if (!key) return false
        return (formData[key] ?? "") !== (initialFormData[key] ?? "")
      }),
    [schema, formData, initialFormData],
  )

  if (!isSuperAdmin || isLoadingProperties || schema.length === 0) {
    return null
  }

  const handleSave = async () => {
    try {
      await completeSetup(formData)
      setIsEditing(false)
    } catch (err) {
      if (!hasErrorMessage(err)) {
        toast.error("Failed to save configuration", {
          toastId: `custom-config-section-save-error-${companyConnectorId}`,
        })
      }
    }
  }

  const handleCancel = () => {
    setFormData(initialFormData)
    setIsEditing(false)
  }

  return (
    <div className="flex flex-col gap-3 mb-4 p-3 border border-system-blue-600 rounded-lg">
      <div className="flex items-center justify-between">
        <p className="text-4 leading-7 font-bold text-platinum">
          Configuration
        </p>
        {!isEditing && (
          <Button
            className="bg-system-blue-700 border border-system-blue-600 text-platinum hover:bg-system-blue-600 rounded-system-md"
            onClick={() => setIsEditing(true)}
          >
            Edit
          </Button>
        )}
      </div>

      <p className="text-3.5 leading-6 font-medium text-gray-400">
        As a super admin, you can change post-setup selections for this
        connector.
      </p>

      {isEditing && (
        <>
          {schema.map(field => (
            <CustomConfigField
              key={field.configuration_path}
              companyConnectorId={companyConnectorId}
              field={field}
              value={formData[field.configuration_path.split(".").pop() || ""]}
              onChange={value => {
                const key = field.configuration_path.split(".").pop()
                if (!key) return
                setFormData(prev => ({ ...prev, [key]: value }))
              }}
            />
          ))}

          <div className="flex justify-end gap-2">
            <Button
              className="bg-system-blue-700 border border-system-blue-600 text-platinum hover:bg-system-blue-600 rounded-system-md"
              onClick={handleCancel}
              disabled={isCompletingSetup}
            >
              Cancel
            </Button>
            <Button
              variant={{ variant: "solid", color: "white" }}
              onClick={() => void handleSave()}
              disabled={!isDirty || isCompletingSetup}
              isLoading={isCompletingSetup}
            >
              Save
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

export default CustomConfigSection
